<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions"
        >
          <!-- Period filter -->
          <div class="me-2 mb-2">
            <label class="form-label mb-0 me-1">Period</label>
            <input
              type="month"
              class="form-control"
              v-model="filters.period"
              @change="fetchReport"
              style="min-width: 180px"
            />
          </div>

          <!-- Export -->
          <div class="mb-2">
            <div class="dropdown">
              <a
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a class="dropdown-item rounded-1" @click.prevent="exportCsv" v-can="'Reports.Export'">
                    <i class="ti ti-file-type-xls me-1"></i>Export as CSV
                  </a>
                </li>
                <li>
                  <a class="dropdown-item rounded-1" @click.prevent="exportPdf" v-can="'Reports.Export'">
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div class="head-icons ms-2">
            <a id="collapse-header" @click="toggleHeader">
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>

      <!-- Summary cards -->
      <div class="row mb-3 dq-stat-grid">
        <div class="col-md-3 d-flex" v-for="card in kpiCards" :key="card.title">
          <div class="card flex-fill dq-stat-card">
            <div class="card-body">
              <div
                class="d-flex align-items-center justify-content-between bg-light border rounded p-2 mb-2"
              >
                <div>
                  <span class="fs-14 fw-normal text-truncate mb-1">
                    {{ card.title }}
                  </span>
                  <h5>
                    <span v-if="card.currency">
                      {{ currency }}
                      {{
                        Number(card.value).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })
                      }}
                    </span>
                    <span v-else>
                      {{ Number(card.value).toLocaleString() }}
                    </span>
                  </h5>
                </div>
                <a
                  href="javascript:void(0);"
                  class="avatar avatar-md avatar-rounded"
                  :class="`bg-transparent-${card.cls} border border-${card.cls}`"
                >
                  <span :class="`text-${card.cls}`">
                    <i :class="card.icon"></i>
                  </span>
                </a>
              </div>
              <p class="fs-12 text-muted mb-0">
                Based on {{ summary?.period || filters.period }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap"
        >
          <h5>Statutory Deduction Report</h5>
        </div>
        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <StatutoryReportTable
              :rows="store.statutoryRows"
              :loading="store.statutoryLoading"
              :currency="currency"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
</template>

<style scoped>
@media (max-width: 767.98px) {
  .dq-page-actions {
    align-items: stretch !important;
  }

  .dq-page-actions > * {
    width: 100%;
    margin-right: 0 !important;
  }

  .dq-page-actions .form-control,
  .dq-page-actions .dropdown,
  .dq-page-actions .dropdown-toggle {
    width: 100%;
  }

  .dq-stat-card .card-body {
    padding: 1rem;
  }

  .custom-datatable-filter.table-responsive {
    margin-left: -1rem;
    margin-right: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
    width: calc(100% + 2rem);
  }
}
</style>

<script setup>
import moment from "moment";
import { reactive, computed, onMounted } from "vue";
import { useEmployeeSalaryStore } from "@/stores/employeeSalaryStore";
import StatutoryReportTable from "./statutory-report-table.vue";

const title = "Statutory Deduction Report";
const text = "Administration";
const text1 = "Payroll Reports";

const store = useEmployeeSalaryStore();

const filters = reactive({
  period: moment().format("YYYY-MM"),
});

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

async function fetchReport() {
  if (!filters.period) return;
  await store.fetchStatutoryReport({ period: filters.period });
}

function exportCsv() {
  store.exportStatutoryReport("csv", { period: filters.period });
}
function exportPdf() {
  store.exportStatutoryReport("pdf", { period: filters.period });
}

const summary = computed(() => store.statutorySummary || null);
const currency = computed(() => summary.value?.currency || "KES");

const kpiCards = computed(() => [
  {
    title: "Total PAYE",
    value: summary.value?.total_paye || 0,
    cls: "danger",
    icon: "ti ti-receipt-tax",
    currency: true,
  },
  {
    title: "Total NSSF",
    value: summary.value?.total_nssf || 0,
    cls: "primary",
    icon: "ti ti-building-bank",
    currency: true,
  },
  {
    title: "Total SHIF",
    value: summary.value?.total_shif || 0,
    cls: "warning",
    icon: "ti ti-heart-rate-monitor",
    currency: true,
  },
  {
    title: "Total Housing Levy",
    value: summary.value?.total_ahl || 0,
    cls: "info",
    icon: "ti ti-home",
    currency: true,
  },
]);

onMounted(() => {
  fetchReport();
});
</script>


